import { useState, useEffect } from "react";
import Modal from "@modalSrc/Modal";
import { IoTrashOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { BsToggles } from "react-icons/bs";
import { FaPlusSquare, FaFileDownload } from "react-icons/fa";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import InvoiceLineItemModal from "@modals/InvoiceLineItemModal";
import PaymentEditModal from "@modals/PaymentEditModal";
import ConfirmModal from "@modals/ConfirmModal";
import Helpers from "@/Helpers";
import DateTimePicker from "@elements/DateTimePicker";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

type FormErrors = Record<string, string | null>;

type InvoiceLineItemLite = {
    id: number;
    chargeDate?: string;
    quantity?: number;
    amount?: number;
    companyFeeItemName?: string;
    entityId?: number;
    description?: string;
};

type InvoicePaymentLite = {
    id: number;
    invoiceId?: number;
    amount?: number;
    paymentDate?: string;
};

type InvoiceModel = {
    id: number;
    companyId: number;
    companyName?: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    periodBeginDate: string;
    periodEndDate: string;
    description: string;
    totalAmount: number;
    status?: number;
    statusId?: number;
    lineItems: InvoiceLineItemLite[];
    payments?: InvoicePaymentLite[];
};

export default function InvoiceDetailModal(props) {
    const { invoiceId, companyId, onClose, updateSpinner, previewMode } = props;
    const [errors, updateErrors] = useState<FormErrors>({});
    const [activeTab, setActiveTab] = useState("details");
    const [selectedLineItems, setSelectedLineItems] = useState<number[]>([]);
    const [invoiceData, setInvoiceData] = useState<InvoiceModel | null>(null);
    const [originalInvoiceData, setOriginalInvoiceData] = useState<InvoiceModel | null>(null);
    const [canApproveInvoice, setCanApproveInvoice] = useState(false);
    const [selectedInvoiceLineItemId, setSelectedInvoiceLineItemId] = useState(null);
    const [showAllLineItems, setShowAllLineItems] = useState(false);
    const [allAvailableLineItems, setAllAvailableLineItems] = useState<InvoiceLineItemLite[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showPeriodConfirm, setShowPeriodConfirm] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);
    useEffect(() => {
        loadInvoiceAsync();
    }, [invoiceId, companyId]);

    // Auto-select line items whenever period dates change
    useEffect(() => {
        if (invoiceData?.periodBeginDate && invoiceData?.periodEndDate) {
            autoSelectLineItemsByPeriod(invoiceData.periodBeginDate, invoiceData.periodEndDate);
        }
    }, [invoiceData?.periodBeginDate, invoiceData?.periodEndDate]);

    if (!invoiceData) return null;

    const statusId = invoiceData.statusId ?? invoiceData.status ?? Enums.INVOICE_STATUS.Unpaid;
    const isLifecycleEditable = !previewMode
        && (statusId === Enums.INVOICE_STATUS.Accruing || statusId === Enums.INVOICE_STATUS.PendingApproval);
    const canApprove = !previewMode && canApproveInvoice;
    const canDownloadPdf = invoiceId > 0 && (
        statusId === Enums.INVOICE_STATUS.Approved
        || statusId === Enums.INVOICE_STATUS.Paid
        || statusId === Enums.INVOICE_STATUS.PartiallyPaid
    );
    const invoiceStatusLabel = (id: number) => {
        switch (id) {
            case Enums.INVOICE_STATUS.Accruing: return "Accruing";
            case Enums.INVOICE_STATUS.PendingApproval: return "Pending approval";
            case Enums.INVOICE_STATUS.Approved: return "Approved";
            case Enums.INVOICE_STATUS.Paid: return "Paid";
            case Enums.INVOICE_STATUS.Unpaid: return "Unpaid";
            case Enums.INVOICE_STATUS.Overdue: return "Overdue";
            case Enums.INVOICE_STATUS.PartiallyPaid: return "Partially paid";
            default: return String(id);
        }
    };

    async function handleApproveInvoice() {
        if (!companyId || !invoiceId) return;
        updateSpinner(true);
        try {
            const resp = await RightTimeApi.postAsync(`/Company/${companyId}/Invoice/${invoiceId}/Approve`, {});
            if (resp?.ok) {
                toast.success("Invoice approved");
                await loadInvoiceAsync();
            } else {
                toast.error("Failed to approve invoice");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error approving invoice");
        } finally {
            updateSpinner(false);
        }
    }

    async function loadInvoiceAsync() {
        if (!companyId) return;

        if (invoiceId === 0) {
            // Reset form for new invoice
            const today = new Date().toISOString().split('T')[0];
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + 30); // Default 30 days from today

            setInvoiceData({
                id: 0,
                companyId: companyId || 0,
                invoiceNumber: '',
                invoiceDate: today,
                dueDate: dueDate.toISOString().split('T')[0],
                periodBeginDate: '',
                periodEndDate: '',
                description: '',
                totalAmount: 0,
                status: Enums.INVOICE_STATUS.Unpaid,
                lineItems: []
            });
            setOriginalInvoiceData({
                id: 0,
                companyId: companyId || 0,
                invoiceNumber: '',
                invoiceDate: today,
                dueDate: dueDate.toISOString().split('T')[0],
                periodBeginDate: '',
                periodEndDate: '',
                description: '',
                totalAmount: 0,
                status: Enums.INVOICE_STATUS.Unpaid,
                lineItems: []
            });
            await getPendingInvoiceLineItemsAsync();
            return;
        }

        try {
            updateSpinner(true);
            const resp = await RightTimeApi.getAsync(`/Company/${companyId}/Invoice/${invoiceId}`);
            updateSpinner(false);
            if (resp && resp.ok && resp.jsonData) {
                const invoice = resp.jsonData;
                const formattedInvoice = {
                    ...invoice,
                    invoiceDate: invoice.invoiceDate ? invoice.invoiceDate.split('T')[0] : '',
                    dueDate: invoice.dueDate ? invoice.dueDate.split('T')[0] : '',
                    periodBeginDate: invoice.periodBeginDate ? invoice.periodBeginDate.split('T')[0] : '',
                    periodEndDate: invoice.periodEndDate ? invoice.periodEndDate.split('T')[0] : ''
                };
                setInvoiceData(formattedInvoice);
                setOriginalInvoiceData(JSON.parse(JSON.stringify(formattedInvoice)));
                setCanApproveInvoice(!!invoice.canApprove);
                // Initialize selectedLineItems with all existing line items
                if (invoice.lineItems && invoice.lineItems.length > 0) {
                    setSelectedLineItems(invoice.lineItems.map(item => item.id));
                }
            } else {
                toast.error("Failed to load invoice details");
                onClose();
            }
        } catch (error) {
            console.error("Error loading invoice:", error);
            toast.error("Error loading invoice details");
            onClose();
        } finally {
            updateSpinner(false);
        }
    }

    function invoiceTotalAmount() {
        if (invoiceData && invoiceData.lineItems) {
            // Only sum selected line items
            return invoiceData.lineItems
                .filter(item => selectedLineItems.includes(item.id))
                .reduce((total, item) => total + (item.quantity * item.amount), 0);
        }
        return 0;
    }

    function invoiceAmountDue() {
        const totalAmount = invoiceTotalAmount() || 0;
        const totalPayments = invoiceData.payments ?
            invoiceData.payments.reduce((total, payment) => total + (payment.amount || 0), 0) : 0;
        return totalAmount - totalPayments;
    }

    function hasUnsavedChanges() {
        if (!originalInvoiceData || !invoiceData) return false;

        // Compare the main invoice fields
        const fieldsToCompare = [
            'invoiceDate', 'dueDate', 'periodBeginDate', 'periodEndDate',
            'description', 'statusId'
        ];

        for (const field of fieldsToCompare) {
            if (originalInvoiceData[field] !== invoiceData[field]) {
                return true;
            }
        }

        // Compare line items count (simple check)
        const originalLineItemIds = originalInvoiceData.lineItems?.map(item => item.id).sort() || [];
        const currentLineItemIds = invoiceData.lineItems?.map(item => item.id).sort() || [];

        if (originalLineItemIds.length !== currentLineItemIds.length) {
            return true;
        }

        // Check if line item IDs are different
        for (let i = 0; i < originalLineItemIds.length; i++) {
            if (originalLineItemIds[i] !== currentLineItemIds[i]) {
                return true;
            }
        }

        return false;
    }

    function handleCloseAttempt() {
        if (hasUnsavedChanges()) {
            setShowCloseConfirm(true);
        } else {
            onClose();
        }
    }

    function handleConfirmClose() {
        setShowCloseConfirm(false);
        onClose();
    }

    function handleCancelClose() {
        setShowCloseConfirm(false);
    }

    function toggleLineItemSelection(itemId) {
        setSelectedLineItems(prev => {
            const isCurrentlySelected = prev.includes(itemId);
            let newSelectedItems;

            if (isCurrentlySelected) {
                newSelectedItems = prev.filter(id => id !== itemId);
            } else {
                newSelectedItems = [...prev, itemId];
            }

            // Update the invoiceData.lineItems array based on selection
            setInvoiceData(prevData => {
                const currentLineItems = prevData.lineItems || [];

                if (isCurrentlySelected) {
                    // Remove item from invoice line items
                    return {
                        ...prevData,
                        lineItems: currentLineItems.filter(item => item.id !== itemId)
                    };
                } else {
                    // Add item to invoice line items
                    // Find the item from the appropriate source
                    const sourceItems = showAllLineItems ? allAvailableLineItems : currentLineItems;
                    const itemToAdd = sourceItems.find(item => item.id === itemId);

                    if (itemToAdd && !currentLineItems.some(item => item.id === itemId)) {
                        return {
                            ...prevData,
                            lineItems: [...currentLineItems, itemToAdd]
                        };
                    }
                }

                return prevData;
            });

            return newSelectedItems;
        });
    }

    function handleInvoiceLineItemClick(lineItemId) {
        setSelectedInvoiceLineItemId(lineItemId);
    }

    function handleInvoiceLineItemModalClose() {
        setSelectedInvoiceLineItemId(null);
        // Reload invoice data to reflect any changes
        loadInvoiceAsync();
    }

    function handleCreateNewInvoiceLineItem() {
        setSelectedInvoiceLineItemId(0);
    }

    function handleCreateNewPayment() {
        setEditingPaymentId(0);
    }

    function handlePaymentClick(paymentId) {
        setEditingPaymentId(paymentId);
    }

    function handlePaymentEditModalClose() {
        setEditingPaymentId(null);
        // Reload invoice data to reflect any changes
        loadInvoiceAsync();
    }

    async function handleToggleAllLineItems() {
        if (!showAllLineItems) {
            // Switching to show all items - load them
            await getAllAvailableLineItemsAsync();
        }
        setShowAllLineItems(!showAllLineItems);
    }

    if (invoiceId === null || invoiceId === undefined) {
        return null;
    }

    function handleInputChange(field, value) {
        setInvoiceData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            // If invoice date is being set, auto-calculate period dates
            if (field === 'invoiceDate' && value) {
                const dateParts = value.split('-'); // "2025-12-01" -> ["2025", "12", "01"]
                if (dateParts.length === 3) {
                    const invoiceYear = parseInt(dateParts[0]);
                    const invoiceMonth = parseInt(dateParts[1]); // 1-based (1 = January)

                    // Set due date to same as invoice date
                    newData.dueDate = value;

                    // Calculate period dates: first day to last day of previous month
                    const periodBegin = new Date(invoiceYear, invoiceMonth - 2, 1);
                    const periodEnd = new Date(invoiceYear, invoiceMonth - 1, 0);

                    newData.periodBeginDate = periodBegin.toISOString().split('T')[0];
                    newData.periodEndDate = periodEnd.toISOString().split('T')[0];
                }
            }

            return newData;
        });

        // Clear error for this field when user starts typing
        if (errors[field]) {
            updateErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    }

    function autoSelectLineItemsByPeriod(beginDate, endDate) {
        console.log('🔍 AutoSelect Debug:', {
            beginDate,
            endDate,
            invoiceDataLineItems: invoiceData?.lineItems?.length || 0,
            allAvailableLineItems: allAvailableLineItems?.length || 0
        });

        if (!beginDate || !endDate) {
            console.log('❌ Missing begin or end date:', { beginDate, endDate });
            return; // Don't filter if either date is missing
        }

        // Get all available line items (from both invoice and pending items)
        const allLineItems = [
            ...(invoiceData?.lineItems || []),
            ...(allAvailableLineItems || [])
        ];

        console.log('📋 All line items before dedup:', allLineItems.length);

        if (allLineItems.length === 0) {
            console.log('⚠️ No line items available for auto-selection. Checking if we need to wait...');
            // If no line items are available yet, try again in a moment
            setTimeout(() => {
                console.log('🔄 Retrying auto-selection after delay...');
                autoSelectLineItemsByPeriod(beginDate, endDate);
            }, 500);
            return;
        }

        // Create ISO strings for the date boundaries
        const beginISO = `${beginDate}T00:00:00.000Z`;
        const endISO = `${endDate}T23:59:59.999Z`;

        console.log('📅 Date boundaries:', { beginISO, endISO });

        // Remove duplicates based on ID
        const uniqueLineItems = allLineItems.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
        );

        console.log('📋 Unique line items:', uniqueLineItems.length);
        console.log('📋 Sample line items:', uniqueLineItems.slice(0, 3).map(item => ({
            id: item.id,
            chargeDate: item.chargeDate,
            description: item.description
        })));

        const itemsInPeriod = uniqueLineItems.filter(item => {
            if (!item.chargeDate) {
                console.log('⚠️ Item missing chargeDate:', item.id);
                return false;
            }

            const chargeDateISO = item.chargeDate.includes('T') ? item.chargeDate : `${item.chargeDate}T00:00:00.000Z`;
            const isInPeriod = chargeDateISO >= beginISO && chargeDateISO <= endISO;

            console.log(`📋 Item ${item.id}: ${item.chargeDate} → ${chargeDateISO} → ${isInPeriod ? '✅' : '❌'}`);

            return isInPeriod;
        });

        const itemIdsInPeriod = itemsInPeriod.map(item => item.id);

        console.log('✅ Items in period:', {
            count: itemsInPeriod.length,
            ids: itemIdsInPeriod
        });

        setSelectedLineItems(itemIdsInPeriod);

        // Calculate and update total amount based on selected line items
        const totalAmount = itemsInPeriod.reduce((sum, item) => {
            return sum + (item.amount || 0);
        }, 0);

        console.log('💰 Calculated total amount:', totalAmount);

        setInvoiceData(prevData => ({
            ...prevData,
            totalAmount: totalAmount
        }));
    }

    function validateForm() {
        const newErrors: FormErrors = {};

        if (!invoiceData.invoiceDate) {
            newErrors.invoiceDate = 'Invoice date is required';
        }

        if (!invoiceData.dueDate) {
            newErrors.dueDate = 'Due date is required';
        } else if (new Date(invoiceData.dueDate) < new Date(invoiceData.invoiceDate)) {
            newErrors.dueDate = 'Due date cannot be before invoice date';
        }

        updateErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleSave() {
        if (validateForm()) {
            if (invoiceId > 0) {
                // Update existing invoice
                await updateInvoiceAsync();
            } else {
                // Create new invoice
                await createInvoiceAsync();
            }
        }
    }

    async function getPendingInvoiceLineItemsAsync() {
        if (!companyId) return;

        try {
            updateSpinner(true);
            const resp = await RightTimeApi.getAsync(`/Company/${companyId}/PendingInvoiceItems`);
            if (resp && resp.ok && resp.jsonData && resp.jsonData.pendingItems) {
                console.log('📋 Loaded pending items:', resp.jsonData.pendingItems.length);
                
                const pendingItems = resp.jsonData.pendingItems;
                
                // Find the oldest line item by chargeDate
                let oldestDate = null;
                if (pendingItems.length > 0) {
                    oldestDate = pendingItems.reduce((oldest, item) => {
                        if (!item.chargeDate) return oldest;
                        if (!oldest) return item.chargeDate;
                        return item.chargeDate < oldest ? item.chargeDate : oldest;
                    }, null);
                }
                
                // Calculate invoice date as first of the month following the oldest line item
                let calculatedInvoiceDate = new Date().toISOString().split('T')[0];
                let calculatedDueDate = new Date();
                calculatedDueDate.setDate(calculatedDueDate.getDate() + 30);
                let calculatedPeriodBegin = '';
                let calculatedPeriodEnd = '';
                
                if (oldestDate) {
                    const oldestDateObj = new Date(oldestDate);
                    // Get the first of the next month
                    const invoiceDateObj = new Date(oldestDateObj.getFullYear(), oldestDateObj.getMonth() + 1, 1);
                    calculatedInvoiceDate = invoiceDateObj.toISOString().split('T')[0];
                    
                    // Set due date to 30 days after invoice date
                    const dueDateObj = new Date(invoiceDateObj);
                    dueDateObj.setDate(dueDateObj.getDate() + 30);
                    calculatedDueDate = dueDateObj;
                    
                    // Calculate period dates based on invoice date
                    const dateParts = calculatedInvoiceDate.split('-');
                    const invoiceYear = parseInt(dateParts[0]);
                    const invoiceMonth = parseInt(dateParts[1]);
                    
                    // Period: first day to last day of previous month
                    const periodBegin = new Date(invoiceYear, invoiceMonth - 2, 1);
                    const periodEnd = new Date(invoiceYear, invoiceMonth - 1, 0);
                    
                    calculatedPeriodBegin = periodBegin.toISOString().split('T')[0];
                    calculatedPeriodEnd = periodEnd.toISOString().split('T')[0];
                }
                
                setInvoiceData(prev => ({
                    ...prev,
                    lineItems: pendingItems,
                    invoiceDate: calculatedInvoiceDate,
                    dueDate: calculatedDueDate.toISOString().split('T')[0],
                    periodBeginDate: calculatedPeriodBegin,
                    periodEndDate: calculatedPeriodEnd
                }));
                
                // Also populate allAvailableLineItems for new invoices
                setAllAvailableLineItems(pendingItems);
            } else {
                toast.error("Failed to load pending invoice items");
            }
        } catch (error) {
            console.error("Error loading pending invoice items:", error);
            toast.error("Error loading pending invoice items");
        } finally {
            updateSpinner(false);
        }
    }

    async function getAllAvailableLineItemsAsync() {
        if (!companyId || !invoiceId || invoiceId === 0) return;

        try {
            updateSpinner(true);
            // Get both the current invoice line items and pending items
            const [invoiceResp, pendingResp] = await Promise.all([
                RightTimeApi.getAsync(`/Company/${companyId}/Invoice/${invoiceId}`),
                RightTimeApi.getAsync(`/Company/${companyId}/PendingInvoiceItems`)
            ]);

            let allItems = [];

            // Add current invoice line items
            if (invoiceResp && invoiceResp.ok && invoiceResp.jsonData && invoiceResp.jsonData.lineItems) {
                allItems = [...invoiceResp.jsonData.lineItems];
            }

            // Add pending items that are not already in the invoice
            if (pendingResp && pendingResp.ok && pendingResp.jsonData && pendingResp.jsonData.pendingItems) {
                const existingIds = allItems.map(item => item.id);
                const newPendingItems = pendingResp.jsonData.pendingItems.filter(item => !existingIds.includes(item.id));
                allItems = [...allItems, ...newPendingItems];
            }

            setAllAvailableLineItems(allItems);
        } catch (error) {
            console.error("Error loading all available line items:", error);
            toast.error("Error loading all available line items");
        } finally {
            updateSpinner(false);
        }
    }

    async function handlePeriodConfirm() {
        // Parse the date strings to create proper date boundaries for filtering
        const beginDateStr = invoiceData.periodBeginDate; // e.g., '2025-10-01'
        const endDateStr = invoiceData.periodEndDate;     // e.g., '2025-10-31'

        if (!beginDateStr || !endDateStr) {
            toast.error("Please set both period begin and end dates");
            return;
        }

        // Create ISO strings for the date boundaries
        // Beginning of the start date (00:00:00 UTC)
        const beginISO = `${beginDateStr}T00:00:00.000Z`;

        // End of the end date (23:59:59.999 UTC)
        const endISO = `${endDateStr}T23:59:59.999Z`;

        const itemsInPeriod = invoiceData.lineItems.filter(item => {
            if (!item.chargeDate) return false;

            // Server dates are already in UTC ISO format, so compare directly as strings
            // or convert to Date objects for comparison
            const chargeDateISO = item.chargeDate.includes('T') ? item.chargeDate : `${item.chargeDate}T00:00:00.000Z`;

            return chargeDateISO >= beginISO && chargeDateISO <= endISO;
        });

        const itemIdsInPeriod = itemsInPeriod.map(item => item.id);
        setSelectedLineItems(itemIdsInPeriod);
        setShowPeriodConfirm(false);

        // Now proceed with creating the invoice, passing the items directly
        await proceedWithInvoiceCreation(itemIdsInPeriod);
    }

    async function proceedWithInvoiceCreation(lineItemIds = null) {
        if (!companyId) {
            toast.error("Company ID is required");
            return;
        }

        // Use passed lineItemIds or fall back to selectedLineItems from state
        const itemsToUse = lineItemIds || selectedLineItems;

        // Map selected items to objects with ID and quantity
        const selectedItemsWithQuantity = itemsToUse.map(itemId => {
            const item = invoiceData.lineItems.find(oli => oli.id === itemId);
            if (!item) {
                console.warn(`Line item with ID ${itemId} not found in invoiceData.lineItems`);
                return {
                    InvoiceLineItemId: itemId,
                    Quantity: 1,
                    Amount: 0
                };
            }
            return {
                InvoiceLineItemId: itemId,
                Quantity: item.quantity || 1,
                Amount: item.amount || 0
            };
        });

        console.log('Selected line items for invoice creation:', selectedItemsWithQuantity);

        const createInvoiceRequest = {
            InvoiceDate: invoiceData.invoiceDate ? new Date(invoiceData.invoiceDate).toISOString() : null,
            DueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate).toISOString() : null,
            PeriodBeginDate: invoiceData.periodBeginDate ? new Date(invoiceData.periodBeginDate).toISOString() : null,
            PeriodEndDate: invoiceData.periodEndDate ? new Date(invoiceData.periodEndDate).toISOString() : null,
            StatusId: invoiceData.status || Enums.INVOICE_STATUS.Unpaid,
            Description: invoiceData.description?.trim() || null,
            InvoiceLineItems: selectedItemsWithQuantity
        };

        console.log('Complete invoice request being sent to API:', createInvoiceRequest);
        console.log('Number of line items being sent:', selectedItemsWithQuantity.length);

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.postAsync(`/Company/${companyId}/Invoice`, createInvoiceRequest);
            updateSpinner(false);

            console.log('API response:', resp);
            if (resp && resp.jsonData) {
                console.log('Created invoice data:', resp.jsonData);
                console.log('Server returned line items count:', resp.jsonData.lineItems?.length || 0);
            }

            if (resp && resp.ok) {
                toast.success("Invoice created successfully");
                onClose();
            } else {
                toast.error("Failed to create invoice");
            }
        } catch (error) {
            console.error("Error creating invoice:", error);
            toast.error("Error creating invoice");
        } finally {
            updateSpinner(false);
        }
    }

    async function createInvoiceAsync() {
        if (!companyId) {
            toast.error("Company ID is required");
            return;
        }

        // Check if period dates are set but no items are selected
        if (invoiceData.periodBeginDate && invoiceData.periodEndDate && selectedLineItems.length === 0) {
            setShowPeriodConfirm(true);
            return;
        }

        // Otherwise proceed with normal creation
        await proceedWithInvoiceCreation();
    }

    async function updateInvoiceAsync() {
        if (!companyId || !invoiceId) {
            toast.error("Company ID and Invoice ID are required");
            return;
        }

        // Convert date fields to ISO format for API, keep everything else as-is
        const dataToSend = {
            ...invoiceData,
            invoiceDate: invoiceData.invoiceDate ? new Date(invoiceData.invoiceDate).toISOString() : null,
            dueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate).toISOString() : null,
            periodBeginDate: invoiceData.periodBeginDate ? new Date(invoiceData.periodBeginDate).toISOString() : null,
            periodEndDate: invoiceData.periodEndDate ? new Date(invoiceData.periodEndDate).toISOString() : null
        };

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.putAsync(`/Company/${companyId}/Invoice/${invoiceId}`, dataToSend);
            updateSpinner(false);
            if (resp && resp.ok) {
                toast.success("Invoice updated successfully");
                onClose();
            } else {
                toast.error("Failed to update invoice");
            }
        } catch (error) {
            console.error("Error updating invoice:", error);
            toast.error("Error updating invoice");
        } finally {
            updateSpinner(false);
        }
    }

    async function deleteInvoiceAsync() {
        if (!companyId || !invoiceId || invoiceId === 0) {
            toast.error("Cannot delete a new invoice");
            return;
        }

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.deleteAsync(`/Company/${companyId}/Invoice/${invoiceId}`);
            updateSpinner(false);
            if (resp && resp.ok) {
                toast.success("Invoice deleted successfully");
                setShowDeleteConfirm(false);
                onClose();
            } else {
                toast.error("Failed to delete invoice");
            }
        } catch (error) {
            console.error("Error deleting invoice:", error);
            toast.error("Error deleting invoice");
        } finally {
            updateSpinner(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCloseAttempt();
        }
    }

    async function handleDownloadPdf() {
        if (!invoiceData || !invoiceData.id || invoiceData.id === 0 || !companyId) {
            toast.error("Unable to download PDF - invoice must be saved first");
            return;
        }
        const sid = invoiceData.statusId ?? invoiceData.status;
        if (sid !== Enums.INVOICE_STATUS.Approved
            && sid !== Enums.INVOICE_STATUS.Paid
            && sid !== Enums.INVOICE_STATUS.PartiallyPaid) {
            toast.error("PDF is available only after the invoice has been approved");
            return;
        }

        try {
            updateSpinner && updateSpinner(true);
            const response = await RightTimeApi.getAsync(`/Company/${companyId}/Invoice/${invoiceData.id}/Pdf`);
            const ok = await downloadResponseAsFile(
                response,
                `invoice_${invoiceData.invoiceNumber || invoiceData.id}.pdf`
            );
            if (ok) toast.success("Invoice PDF downloaded successfully");
            else toast.error("Failed to download invoice PDF");
        } catch (error) {
            console.error("Error downloading invoice PDF:", error);
            toast.error("Error downloading invoice PDF");
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    if (!companyId) {
        return (
            <Modal
                isOpen={true}
                title="Error"
                onClose={onClose}
                className="invoiceDetailModal"
                footer={(
                    <div className="modalFooter">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                )}
            >
                <p>Company ID is required to create an invoice.</p>
            </Modal>
        );
    }

    return (
        <>
            <Modal
                isOpen={true}
                title={invoiceId > 0 ? `Edit Invoice (${invoiceId})` : "Create New Invoice"}
                onClose={handleCloseAttempt}
                className="invoiceDetailModal"
                footer={(
                    <div className="modalFooter">
                        <div className="footerLeft">
                            <button
                                type="button"
                                className="btn btn-danger iconButton"
                                onClick={() => setShowDeleteConfirm(true)}
                                title="Delete Invoice"
                                disabled={invoiceId === 0}
                            >
                                <IoTrashOutline />
                            </button>
                            <button
                                type="button"
                                className="btn btn-info iconButton"
                                onClick={handleDownloadPdf}
                                title="Download PDF (after approval)"
                                disabled={invoiceId === 0 || !canDownloadPdf}
                            >
                                <FaFileDownload />
                            </button>
                        </div>
                        <div className="footerRight">
                            {canApprove && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleApproveInvoice}
                                >
                                    Approve Invoice
                                </button>
                            )}
                            <button
                                className="btn btn-secondary"
                                onClick={handleCloseAttempt}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={invoiceId > 0 && !isLifecycleEditable
                                    && statusId !== Enums.INVOICE_STATUS.Unpaid
                                    && statusId !== Enums.INVOICE_STATUS.Overdue}
                            >
                                {invoiceId > 0 ? 'Update' : 'Create'} Invoice
                            </button>
                        </div>
                    </div>
                )}
            >

                {/* Tab Navigation */}
                <div className="tabHeader">
                    <span
                        className={`tab ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Invoice Details
                    </span>
                    <span
                        className={`tab ${activeTab === 'items' ? 'active' : ''}`}
                        onClick={() => setActiveTab('items')}
                    >
                        Invoice Items
                        {invoiceData.lineItems.length > 0 && <span className="badge">{invoiceData.lineItems.length}</span>}
                    </span>
                    <span
                        className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payments')}
                    >
                        Payments
                        {invoiceData.payments && invoiceData.payments.length > 0 && <span className="badge">{invoiceData.payments.length}</span>}
                    </span>
                </div>

                <div className="modalBody">
                    <div
                        style={{ display: activeTab === 'details' ? 'block' : 'none' }}
                    >
                        <div className="horizontal tableWrapper factTable">
                            <table className="detailsTable formTable">
                                <caption>
                                  <span className="title">Invoice Details</span>
                                </caption>
                                <tbody>
                                    <tr>
                                        <th className="label right">Invoice Number:</th>
                                        <td>
                                            <input
                                                id="invoiceNumber"
                                                type="text"
                                                value={invoiceId === 0 ? "tbd" : invoiceId.toString()}
                                                readOnly
                                                className="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right">Company:</th>
                                        <td>
                                            <span>{invoiceData.companyName || 'Loading...'}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="invoiceDate">Invoice Date *:</label></th>
                                        <td>
                                            <DateTimePicker
                                                id="invoiceDate"
                                                field="invoiceDate"
                                                pickerTitle="Invoice Date"
                                                value={invoiceData.invoiceDate}
                                                onChange={(e) => handleInputChange('invoiceDate', e?.target?.value || '')}
                                                className={errors.invoiceDate ? 'error' : ''}
                                                dateOnly
                                            />
                                            {errors.invoiceDate && <span className="errorText">{errors.invoiceDate}</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="dueDate">Due Date *:</label></th>
                                        <td>
                                            <DateTimePicker
                                                id="dueDate"
                                                field="dueDate"
                                                pickerTitle="Due Date"
                                                value={invoiceData.dueDate}
                                                onChange={(e) => handleInputChange('dueDate', e?.target?.value || '')}
                                                className={errors.dueDate ? 'error' : ''}
                                                dateOnly
                                            />
                                            {errors.dueDate && <span className="errorText">{errors.dueDate}</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="periodBeginDate">Period Begin Date:</label></th>
                                        <td>
                                            <DateTimePicker
                                                id="periodBeginDate"
                                                field="periodBeginDate"
                                                pickerTitle="Period Begin Date"
                                                value={invoiceData.periodBeginDate || ''}
                                                onChange={(e) => handleInputChange('periodBeginDate', e?.target?.value || '')}
                                                className={errors.periodBeginDate ? 'error' : ''}
                                                dateOnly
                                            />
                                            {errors.periodBeginDate && <span className="errorText">{errors.periodBeginDate}</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="periodEndDate">Period End Date:</label></th>
                                        <td>
                                            <DateTimePicker
                                                id="periodEndDate"
                                                field="periodEndDate"
                                                pickerTitle="Period End Date"
                                                value={invoiceData.periodEndDate || ''}
                                                onChange={(e) => handleInputChange('periodEndDate', e?.target?.value || '')}
                                                className={errors.periodEndDate ? 'error' : ''}
                                                dateOnly
                                            />
                                            {errors.periodEndDate && <span className="errorText">{errors.periodEndDate}</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="description">Description:</label></th>
                                        <td>
                                            <textarea
                                                id="description"
                                                value={invoiceData.description}
                                                onChange={(e) => handleInputChange('description', e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                placeholder="Enter invoice description (optional)"
                                                className={errors.description ? 'error' : ''}
                                                rows={3}
                                            />
                                            {errors.description && <span className="errorText">{errors.description}</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="totalAmount">Total Amount:</label></th>
                                        <td>
                                            <span>{Helpers.formatCurrency(invoiceTotalAmount())}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="amountDue">Amount Due:</label></th>
                                        <td>
                                            <span>{Helpers.formatCurrency(invoiceAmountDue())}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="label right"><label htmlFor="status">Status:</label></th>
                                        <td>
                                            {isLifecycleEditable || (statusId >= Enums.INVOICE_STATUS.Accruing && statusId <= Enums.INVOICE_STATUS.Approved) ? (
                                                <span>{invoiceStatusLabel(statusId)}</span>
                                            ) : (
                                                <select
                                                    id="status"
                                                    value={invoiceData.statusId}
                                                    onChange={(e) => handleInputChange('statusId', parseInt(e.target.value))}
                                                >
                                                    <option value={Enums.INVOICE_STATUS.Unpaid}>Unpaid</option>
                                                    <option value={Enums.INVOICE_STATUS.Paid}>Paid</option>
                                                    <option value={Enums.INVOICE_STATUS.Overdue}>Overdue</option>
                                                    <option value={Enums.INVOICE_STATUS.PartiallyPaid}>Partially Paid</option>
                                                </select>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div
                        style={{ display: activeTab === 'items' ? 'block' : 'none' }}
                    >
                        {!invoiceId ? (
                            <div className="invoiceItemsTab">
                                <div className="tableWrapper dimensionTable scrollable invoiceItemsTable">
                                    <table>
                                        <caption>
                                          <span className="title">Invoice Line Items</span>
                                            {selectedLineItems.length > 0 && <span className="badge">{selectedLineItems.length}</span>}
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th><BsToggles /></th>
                                                <th>ID</th>
                                                <th>Date</th>
                                                <th>Quantity</th>
                                                <th>Amount</th>
                                                <th>Fee Item</th>
                                                <th>Item Id</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceData?.lineItems?.length > 0 ? (
                                                invoiceData?.lineItems.sort((a, b) => new Date(a.chargeDate).getTime() - new Date(b.chargeDate).getTime()).map(item => (
                                                    <tr key={item.id}>
                                                        <td className="center" onClick={() => toggleLineItemSelection(item.id)} style={{ cursor: 'pointer' }}>
                                                            <span>
                                                                {selectedLineItems.includes(item.id) ? (
                                                                    <MdCheckBox className="checkbox checked actionIcon" />
                                                                ) : (
                                                                    <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td>{item.id}</td>
                                                        <td>{Helpers.formatDate(item.chargeDate)}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{Helpers.formatCurrency(item.amount)}</td>
                                                        <td className="left">{item.companyFeeItemName}</td>
                                                        <th>{item.entityId}</th>
                                                        <td className="left">{item.description}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={9} className="center">No open invoice line items found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="invoiceItemsTab">
                                <div className="tableWrapper dimensionTable scrollable invoiceItemsTable">
                                    <table>
                                        <caption>
                                          <span className="title">Invoice Items</span>
                                            {selectedLineItems.length > 0 && <span className="badge">{selectedLineItems.length}</span>}
                                            <span
                                                className="actionIcon plusIcon captionRight"
                                                onClick={handleCreateNewInvoiceLineItem}
                                                title="Create New Line Item"
                                                style={{ cursor: 'pointer', display: 'inline-block' }}
                                            >
                                                <FaPlusSquare />
                                            </span>
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <BsToggles
                                                        title="Toggle viewing all available line items"
                                                        onClick={handleToggleAllLineItems}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </th>
                                                <th>ID</th>
                                                <th>Date</th>
                                                <th>Quantity</th>
                                                <th>Amount</th>
                                                <th>Fee Item</th>
                                                <th>Item Id</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const itemsToShow = showAllLineItems ? allAvailableLineItems : invoiceData.lineItems;

                                                // Filter items to show only those on the invoice or within the date window
                                                const filteredItems = itemsToShow?.filter(item => {
                                                    // Always include items that are already on the invoice
                                                    if (invoiceData.lineItems?.some(invoiceItem => invoiceItem.id === item.id)) {
                                                        return true;
                                                    }

                                                    // If no date window is set, don't include items not on invoice
                                                    if (!invoiceData.periodBeginDate || !invoiceData.periodEndDate) {
                                                        return false;
                                                    }

                                                    // Include items within the date window
                                                    if (item.chargeDate) {
                                                        const chargeDate = new Date(item.chargeDate);
                                                        const beginDate = new Date(invoiceData.periodBeginDate);
                                                        const endDate = new Date(invoiceData.periodEndDate);
                                                        return chargeDate >= beginDate && chargeDate <= endDate;
                                                    }

                                                    return false;
                                                }) || [];

                                                return filteredItems.length > 0 ? (
                                                    filteredItems.sort((a, b) => new Date(a.chargeDate).getTime() - new Date(b.chargeDate).getTime()).map(item => (
                                                        <tr className="pointer" key={item.id} onClick={() => handleInvoiceLineItemClick(item.id)}>
                                                            <td className="center" onClick={(e) => { e.stopPropagation(); toggleLineItemSelection(item.id); }} style={{ cursor: 'pointer' }}>
                                                                <span>
                                                                    {selectedLineItems.includes(item.id) ? (
                                                                        <MdCheckBox className="checkbox checked actionIcon" />
                                                                    ) : (
                                                                        <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td>{item.id}</td>
                                                            <td className="left">{Helpers.formatDate(item.chargeDate)}</td>
                                                            <td className="center">{item.quantity}</td>
                                                            <td className="right">{Helpers.formatCurrency(item.amount)}</td>
                                                            <td className="left">{item.companyFeeItemName}</td>
                                                            <td className="left">{item.entityId}</td>
                                                            <td className="left">{item.description}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={9} className="center">
                                                            {showAllLineItems ?
                                                                (invoiceData.periodBeginDate && invoiceData.periodEndDate ?
                                                                    "No available line items found within the invoice period" :
                                                                    "No available line items found (set period dates to see available items)") :
                                                                "No invoice items found"}
                                                        </td>
                                                    </tr>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        style={{ display: activeTab === 'payments' ? 'block' : 'none' }}
                    >
                        <div className="paymentsTab">
                            <div className="tableWrapper dimensionTable scrollable paymentsTable">
                                <table>
                                    <caption>
                                      <span className="title">Payments</span>
                                        {invoiceData.payments && invoiceData.payments.length > 0 && <span className="badge">{invoiceData.payments.length}</span>}
                                        <span
                                            className="actionIcon plusIcon captionRight"
                                            onClick={handleCreateNewPayment}
                                            title="Record Payment"
                                            style={{ cursor: 'pointer', display: 'inline-block' }}
                                        >
                                            <FaPlusSquare />
                                        </span>
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th>Payment ID</th>
                                            <th>Invoice ID</th>
                                            <th>Amount</th>
                                            <th>Payment Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceData.payments && invoiceData.payments.length > 0 ? (
                                            invoiceData.payments.map(payment => (
                                                <tr
                                                    key={payment.id}
                                                    onClick={() => handlePaymentClick(payment.id)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to edit payment"
                                                >
                                                    <td>{payment.id}</td>
                                                    <td>{payment.invoiceId}</td>
                                                    <td className="right">{Helpers.formatCurrency(payment.amount)}</td>
                                                    <td>{Helpers.formatDate(payment.paymentDate)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4} className="center">No payments found for this invoice</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

            </Modal>

            {selectedInvoiceLineItemId !== null && (
                <InvoiceLineItemModal
                    invoiceLineItemId={selectedInvoiceLineItemId}
                    companyId={companyId}
                    invoiceId={invoiceId}
                    adminPerms={null}
                    onClose={handleInvoiceLineItemModalClose}
                    updateSpinner={updateSpinner}
                    onSave={handleInvoiceLineItemModalClose}
                />
            )}

            {editingPaymentId !== null && (
                <PaymentEditModal
                    paymentId={editingPaymentId}
                    companyId={companyId}
                    invoiceId={invoiceId}
                    onClose={handlePaymentEditModalClose}
                    updateSpinner={updateSpinner}
                />
            )}

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={deleteInvoiceAsync}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Invoice"
                message={`Are you sure you want to delete this invoice? This action cannot be undone. All associated payment allocations will be removed and all invoice line items will be disassociated with this invoice.`}
                confirmText="Yes, Delete Invoice"
                dangerText="This will permanently remove the invoice and all associated data."
            />

            <ConfirmModal
                isOpen={showPeriodConfirm}
                onConfirm={handlePeriodConfirm}
                onCancel={async () => {
                    setShowPeriodConfirm(false);
                    await proceedWithInvoiceCreation();
                }}
                title="Add Items for Period"
                message={`You have set period dates but haven't selected any items. Would you like to automatically add all available invoice items between ${invoiceData.periodBeginDate} and ${invoiceData.periodEndDate}?`}
                confirmText="Yes, Add Period Items"
                cancelText="No, Create Empty Invoice"
            />

            <ConfirmModal
                isOpen={showCloseConfirm}
                onConfirm={handleConfirmClose}
                onCancel={handleCancelClose}
                title="Unsaved Changes"
                message="You have unsaved changes. Are you sure you want to close without saving?"
                confirmText="Yes, Close Without Saving"
                cancelText="Cancel"
            />
        </>
    );
}

